import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { MiniLoader } from '@ge/components/loader';
import { DraggableTable, DynamicTable } from '@ge/components/table';
import { TooltipCell } from '@ge/components/table/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { ManageDefs } from '@ge/models/constants';
import { SeverityType } from '@ge/models/constants';
import { useFilterDefs } from '@ge/shared/hooks';
import { withDefault } from '@ge/shared/util/table-utils';
import { elevations } from '@ge/tokens/elevations';

import {
  TurbineHistoryColumnDefs,
  TurbineHistoryColumns,
  defaultTurbineHistoryCols,
} from '../../models/table-col-defs/turbine-history-table-cols';

const StyledTimeDate = styled.div`
  span {
    display: inline-block;

    &:first-of-type {
      margin-right: 11px;
    }

    &:last-of-type {
      color: ${(props) => props.theme.table.textLightColor};
      font-size: 11px;
    }
  }
`;
const StyledSeverity = styled.span`
  font-size: 12px;
  vertical-align: middle;
`;

const StyledDesc = styled.span`
  margin-left: 6px;
`;

const Load = styled.div`
  top: 400px;
  position: relative;
`;

const SeverityIcon = styled.svg`
  display: inline-block;
  padding-top: 2px;
  vertical-align: middle;
  height: 22px;
  width: 16px;
  & path:nth-child(1) {
    fill: ${(props) => props.theme.eventSeverity.safetyCritical};
  }
  & path:nth-child(2),
  path:nth-child(3) {
    fill: ${(props) => props.theme.eventSeverity.safetyCriticalExclamatory};
  }
`;

const AlarmIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.FAULT_ALARM,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.falutAlarm};
  }
`;

const WarningIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.WARNING_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.warning};
  }
`;

const InformationIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.INFORMATIONAL_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.informational};
  }
`;

const TurbineEventsHistoryTable = ({
  isLoading,
  tableType,
  history,
  columns,
  sortAction,
  sortMetric,
  sortDirection,
  onEventSelect,
  draggable,
  scrollable,
  onDrop,
  onFilter,
  filterValues,
  onFilterChange,
}) => {
  const { t, ready } = useTranslation(['monitor.issues'], {
    useSuspense: false,
  });

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  const [tableColumns, setTableColumns] = useState(columns);

  const { filterDefs, onChange: onFilterApply } = useFilterDefs({
    columnDefs: TurbineHistoryColumnDefs,
    stateId: ManageDefs.TURBINE_EVENTS_HISTORY,
  });

  const handleFilterApply = useCallback(
    (_, columnKey, value) => {
      onFilterApply(columnKey, value);
    },
    [onFilterApply],
  );

  const handleFilterChange = useCallback(
    (_, key, value) => onFilterChange({ key, value }),
    [onFilterChange],
  );

  useEffect(() => {
    onFilter(filterDefs);
  }, [filterDefs, onFilter]);

  useEffect(() => {
    const filteredColumns = [];
    columns.map((c) => {
      const newCol = { id: c.id, cols: [] };
      const cols = c.cols.filter((f) => f.filter === undefined || f.filter === tableType);
      if (cols.length > 0) {
        newCol.cols = cols;
        filteredColumns.push(newCol);
      }
    });
    setTableColumns(filteredColumns);
  }, [columns, tableType, setTableColumns]);

  const cellValueMapFn = useCallback((alert) => {
    if (!alert) {
      return {};
    }
    const { eventClass, code, desc, startDate, endDate } = alert;

    return {
      [TurbineHistoryColumns.CODE]: code,
      [TurbineHistoryColumns.EVENT_CLASS]: eventClass,
      [TurbineHistoryColumns.DESCRIPTION]: desc,
      [TurbineHistoryColumns.START]: startDate,
      [TurbineHistoryColumns.END]: endDate,
    };
  }, []);

  const getIconType = useCallback((severity) => {
    switch (severity) {
      case SeverityType.ALARM:
        return <AlarmIcon />;
      case SeverityType.WARNING:
        return <WarningIcon />;
      case SeverityType.INFORMATION:
        return <InformationIcon />;
      case SeverityType.CRITICAL:
        return (
          <SeverityIcon viewBox="0 0 16 12">
            <path d={Icons.SAFETY_POLYGON} />
            <path d={Icons.SAFETY_EXCLAMATORY} />
            <path d={Icons.SAFETY_CIRCLE} />
          </SeverityIcon>
        );
      default:
        return '';
    }
  }, []);

  const customCellFn = useCallback(
    (columnKey, cellValue) => {
      switch (columnKey) {
        case TurbineHistoryColumns.CODE:
          return withDefault(cellValue);

        case TurbineHistoryColumns.EVENT_CLASS:
          return withDefault(
            cellValue,
            <StyledSeverity>
              <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
                {getIconType(cellValue)}
              </TooltipCell>
            </StyledSeverity>,
          );

        case TurbineHistoryColumns.DESCRIPTION:
          return withDefault(
            cellValue,
            <TooltipCell tooltip={cellValue} zIndex={elevations.P20}>
              <StyledDesc>{cellValue}</StyledDesc>
            </TooltipCell>,
          );

        case TurbineHistoryColumns.START:
          return withDefault(
            cellValue,
            <StyledTimeDate>
              <span>{dayjs(cellValue * 1000).format('HH:mm:ss')}</span>
              <span>{dayjs(cellValue * 1000).format('DD MMM YY')}</span>
            </StyledTimeDate>,
          );

        case TurbineHistoryColumns.END:
          return withDefault(
            cellValue,
            <StyledTimeDate>
              <span>{dayjs(cellValue * 1000).format('HH:mm:ss')}</span>
              <span>{dayjs(cellValue * 1000).format('DD MMM YY')}</span>
            </StyledTimeDate>,
          );
        default:
          return null;
      }
    },
    [getIconType],
  );

  /**
   * Bootstrap table factories
   */
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: TurbineHistoryColumnDefs,
    cellValueMapFn,
    customCellFn,
    sortAction,
    sortedDirection,
    draggable,
    filterValues,
    onFilterApply: handleFilterApply,
    onFilterChange: handleFilterChange,
    filters: filterDefs,
  });
  if (!columns || columns.length === 0) {
    return null;
  }
  const dynamicProps = {
    scrollable,
    compressed: true,
    transparent: true,
    noTitles: true,
    columns: tableColumns,
    columnGroupFactory,
    columnFactory,
    cellFactory,
    sortAction,
    values: history,
    onValueSelect: onEventSelect,
    rowKeyProperty: 'id',
    dropHandler: draggable ? onDrop : () => null,
  };

  if (!ready || isLoading) {
    return (
      <>
        <Load>
          <MiniLoader />
        </Load>
      </>
    );
  }

  return React.createElement(draggable ? DraggableTable : DynamicTable, dynamicProps);
};

TurbineEventsHistoryTable.propTypes = {
  isLoading: PropTypes.bool,
  tableType: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  history: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  sortAction: PropTypes.func,
  sortMetric: PropTypes.string,
  sortDirection: PropTypes.string,
  onEventSelect: PropTypes.func,
  onDrop: PropTypes.func,
  scrollable: PropTypes.bool,
  draggable: PropTypes.bool,
  filterValues: PropTypes.object,
  onFilter: PropTypes.func,
  onFilterChange: PropTypes.func,
};

TurbineEventsHistoryTable.defaultProps = {
  columns: defaultTurbineHistoryCols,
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onEventSelect: () => null,
  onDrop: () => null,
  scrollable: false,
  draggable: false,
  filterValues: {},
  onFilter: () => null,
  onFilterChange: () => null,
};

export default TurbineEventsHistoryTable;
